/**
 * Tasks Store - Zustand
 * Cf. Nova-V3 projectTasksStore (adapté CIME).
 * Gère la state UI : view mode, animations, formulaire de création, expanded subtasks.
 * Les données serveur restent dans React Query (useTasks).
 */
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export type ViewMode = 'list' | 'board' | 'calendar';
export type GroupMode = 'sections' | 'focus';
export type SortMode = 'manual' | 'due-asc' | 'due-desc';

export interface TaskTag {
  label: string;
  color: string;
}

export interface NewTaskForm {
  content: string;
  description: string;
  priority: number;
  due_date: string;
  due_time: string;
  duration: string;
}

const DEFAULT_TASK_FORM: NewTaskForm = {
  content: '',
  description: '',
  priority: 3,
  due_date: '',
  due_time: '',
  duration: '',
};

interface TasksState {
  // Active dossier (persisted)
  activeDossierId: string | null;
  setActiveDossierId: (id: string | null) => void;

  // Filtre par tags (persisted) — identifiants de task_tags, plus la
  // sentinelle NO_TAG pour « sans tag ». Sans rapport avec `taskTags`
  // ci-dessous, qui appartient au formulaire de création.
  tagFilterIds: string[];
  setTagFilterIds: (ids: string[]) => void;

  // Affichage vue liste (persisted)
  groupMode: GroupMode;
  setGroupMode: (m: GroupMode) => void;
  sortMode: SortMode;
  setSortMode: (m: SortMode) => void;

  // Suppressions en attente d'annulation (NON persisté — un reload annule
  // les timers, les tâches réapparaissent, aucune perte).
  pendingDeleteIds: Set<string>;
  markPendingDelete: (id: string) => void;
  unmarkPendingDelete: (id: string) => void;

  // Sélection multiple (NON persisté) — Lot 4.
  selectedTaskIds: Set<string>;
  selectionAnchorId: string | null;
  toggleSelected: (id: string) => void;
  selectMany: (ids: string[], anchorId: string) => void;
  clearSelection: () => void;

  // Animation state
  newTaskIds: Set<string>;
  removedTaskIds: Set<string>;
  completingTaskId: string | null;

  // View state
  viewMode: ViewMode;
  showArchivedTasks: boolean;
  // Afficher les tâches terminées dans les vues (persisted).
  showCompletedTasks: boolean;
  setShowCompletedTasks: (show: boolean) => void;
  expandedTasks: Set<string>;

  // Form state
  selectedSectionId: string | null;
  newTaskForm: NewTaskForm;
  taskTags: TaskTag[];

  // Animations
  markTaskAsNew: (taskId: string) => void;
  clearNewTaskMark: (taskId: string) => void;
  markTaskAsRemoved: (taskId: string) => void;
  clearRemovedTaskMark: (taskId: string) => void;
  setCompletingTaskId: (taskId: string | null) => void;
  cleanupAnimations: () => void;

  // View
  setViewMode: (mode: ViewMode) => void;
  setShowArchivedTasks: (show: boolean) => void;
  toggleExpandTask: (taskId: string) => void;
  setExpandedTasks: (ids: Set<string>) => void;

  // Form
  setSelectedSectionId: (id: string | null) => void;
  updateNewTaskForm: <K extends keyof NewTaskForm>(key: K, value: NewTaskForm[K]) => void;
  resetNewTaskForm: () => void;
  addTaskTag: (tag: TaskTag) => void;
  removeTaskTag: (index: number) => void;
  clearTaskTags: () => void;
}

export const useTasksStore = create<TasksState>()(
  devtools(
    persist(
      (set) => ({
        activeDossierId: null,
        setActiveDossierId: (id) => set({ activeDossierId: id }),

        tagFilterIds: [],
        setTagFilterIds: (ids) => set({ tagFilterIds: ids }),

        groupMode: 'sections',
        setGroupMode: (m) => set({ groupMode: m }),
        sortMode: 'manual',
        setSortMode: (m) => set({ sortMode: m }),

        pendingDeleteIds: new Set(),
        markPendingDelete: (id) =>
          set((s) => ({ pendingDeleteIds: new Set(s.pendingDeleteIds).add(id) })),
        unmarkPendingDelete: (id) =>
          set((s) => {
            const next = new Set(s.pendingDeleteIds);
            next.delete(id);
            return { pendingDeleteIds: next };
          }),

        selectedTaskIds: new Set(),
        selectionAnchorId: null,
        toggleSelected: (id) =>
          set((s) => {
            const next = new Set(s.selectedTaskIds);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return { selectedTaskIds: next, selectionAnchorId: id };
          }),
        selectMany: (ids, anchorId) =>
          set((s) => {
            const next = new Set(s.selectedTaskIds);
            ids.forEach((i) => next.add(i));
            return { selectedTaskIds: next, selectionAnchorId: anchorId };
          }),
        clearSelection: () => set({ selectedTaskIds: new Set(), selectionAnchorId: null }),

        newTaskIds: new Set(),
        removedTaskIds: new Set(),
        completingTaskId: null,

        viewMode: 'list',
        showArchivedTasks: false,
        showCompletedTasks: false,
        expandedTasks: new Set(),

        selectedSectionId: null,
        newTaskForm: DEFAULT_TASK_FORM,
        taskTags: [],

        markTaskAsNew: (taskId) =>
          set((s) => ({ newTaskIds: new Set(s.newTaskIds).add(taskId) })),
        clearNewTaskMark: (taskId) =>
          set((s) => {
            const next = new Set(s.newTaskIds);
            next.delete(taskId);
            return { newTaskIds: next };
          }),
        markTaskAsRemoved: (taskId) =>
          set((s) => ({ removedTaskIds: new Set(s.removedTaskIds).add(taskId) })),
        clearRemovedTaskMark: (taskId) =>
          set((s) => {
            const next = new Set(s.removedTaskIds);
            next.delete(taskId);
            return { removedTaskIds: next };
          }),
        setCompletingTaskId: (taskId) => set({ completingTaskId: taskId }),
        cleanupAnimations: () =>
          set({
            newTaskIds: new Set(),
            removedTaskIds: new Set(),
            completingTaskId: null,
          }),

        setViewMode: (mode) => set({ viewMode: mode }),
        setShowArchivedTasks: (show) => set({ showArchivedTasks: show }),
        setShowCompletedTasks: (show) => set({ showCompletedTasks: show }),
        toggleExpandTask: (taskId) =>
          set((s) => {
            const next = new Set(s.expandedTasks);
            if (next.has(taskId)) next.delete(taskId);
            else next.add(taskId);
            return { expandedTasks: next };
          }),
        setExpandedTasks: (ids) => set({ expandedTasks: ids }),

        setSelectedSectionId: (id) => set({ selectedSectionId: id }),
        updateNewTaskForm: (key, value) =>
          set((s) => ({ newTaskForm: { ...s.newTaskForm, [key]: value } })),
        resetNewTaskForm: () =>
          set({
            newTaskForm: DEFAULT_TASK_FORM,
            taskTags: [],
            selectedSectionId: null,
          }),
        addTaskTag: (tag) => set((s) => ({ taskTags: [...s.taskTags, tag] })),
        removeTaskTag: (index) =>
          set((s) => ({ taskTags: s.taskTags.filter((_, i) => i !== index) })),
        clearTaskTags: () => set({ taskTags: [] }),
      }),
      {
        name: 'cime-tasks-store',
        partialize: (state) => ({
          viewMode: state.viewMode,
          showArchivedTasks: state.showArchivedTasks,
          showCompletedTasks: state.showCompletedTasks,
          activeDossierId: state.activeDossierId,
          tagFilterIds: state.tagFilterIds,
          groupMode: state.groupMode,
          sortMode: state.sortMode,
        }),
      },
    ),
    { name: 'TasksStore' },
  ),
);

export const selectViewMode = (s: TasksState) => s.viewMode;
export const selectShowArchivedTasks = (s: TasksState) => s.showArchivedTasks;
export const selectActiveDossierId = (s: TasksState) => s.activeDossierId;
export const selectTagFilterIds = (s: TasksState) => s.tagFilterIds;
